'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { disableReactDevTools } from '@/lib/disable-react-devtools';

const STORAGE_KEY = 'scoreeze.contentProtection.enabled';
const CHANGE_EVENT = 'scoreeze:content-protection-changed';

type ContentProtectionWindow = Window & {
  setContentProtectionEnabled?: (enabled: boolean) => void;
  getContentProtectionEnabled?: () => boolean;
};

type ContentProtectionProps = {
  excludedPaths?: string[];
};

function getDefaultProtectionState(): boolean {
  return process.env.NEXT_PUBLIC_ENABLE_CONTENT_PROTECTION === 'true';
}

function getCurrentProtectionState(): boolean {
  if (typeof window === 'undefined') {
    return getDefaultProtectionState();
  }

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'true') return true;
  if (stored === 'false') return false;

  return getDefaultProtectionState();
}

function getExcludedPathsFromEnv(): string[] {
  const raw = process.env.NEXT_PUBLIC_CONTENT_PROTECTION_EXCLUDED_PATHS || '';
  return raw
    .split(',')
    .map((path) => path.trim())
    .filter((path) => path.length > 0);
}

function normalizePath(path: string): string {
  if (!path) return '/';
  const withLeadingSlash = path.startsWith('/') ? path : `/${path}`;
  return withLeadingSlash.length > 1 ? withLeadingSlash.replace(/\/+$/, '') : withLeadingSlash;
}

function isPathExcluded(pathname: string, excludedPaths: string[]): boolean {
  const currentPath = normalizePath(pathname);

  return excludedPaths.some((rawPattern) => {
    const pattern = normalizePath(rawPattern.trim());
    if (!pattern) return false;

    if (pattern.endsWith('/*')) {
      const basePattern = pattern.slice(0, -2);
      return currentPath === basePattern || currentPath.startsWith(`${basePattern}/`);
    }

    return currentPath === pattern;
  });
}

export function ContentProtection({ excludedPaths = [] }: ContentProtectionProps) {
  const [isEnabled, setIsEnabled] = useState(false);
  const pathname = usePathname() || '/';
  const configuredExcludedPaths = [...getExcludedPathsFromEnv(), ...excludedPaths];
  const isExcluded = isPathExcluded(pathname, configuredExcludedPaths);

  useEffect(() => {
    const syncState = () => setIsEnabled(getCurrentProtectionState());
    const typedWindow = window as ContentProtectionWindow;

    typedWindow.setContentProtectionEnabled = (enabled: boolean) => {
      window.localStorage.setItem(STORAGE_KEY, String(enabled));
      window.dispatchEvent(new Event(CHANGE_EVENT));
    };

    typedWindow.getContentProtectionEnabled = () => getCurrentProtectionState();

    syncState();
    window.addEventListener('storage', syncState);
    window.addEventListener(CHANGE_EVENT, syncState);

    return () => {
      window.removeEventListener('storage', syncState);
      window.removeEventListener(CHANGE_EVENT, syncState);
      delete typedWindow.setContentProtectionEnabled;
      delete typedWindow.getContentProtectionEnabled;
    };
  }, []);

  useEffect(() => {
    if (!isEnabled || isExcluded) {
      document.body.classList.remove('content-protection-enabled');
      return;
    }

    disableReactDevTools();
    document.body.classList.add('content-protection-enabled');

    const isEditableTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof Element)) return false;
      const editableSelector = [
        'input',
        'textarea',
        'select',
        '[contenteditable="true"]',
        '[contenteditable=""]',
        '.ql-editor',
        '.ql-container',
      ].join(', ');
      return !!target.closest(editableSelector);
    };

    const isAllowedDragTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof Element)) return false;
      return !!target.closest('[draggable="true"], [data-content-protection-allow-drag="true"]');
    };

    const preventDefault = (event: Event) => {
      if (isEditableTarget(event.target)) {
        return;
      }
      event.preventDefault();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const key = (typeof event.key === 'string' ? event.key : '').toLowerCase();
      const hasPrimaryModifier = event.ctrlKey || event.metaKey;
      const inEditable = isEditableTarget(event.target);

      if (key === 'f12') {
        event.preventDefault();
        return;
      }

      if (hasPrimaryModifier && event.shiftKey && ['i', 'j', 'c', 'k'].includes(key)) {
        event.preventDefault();
        return;
      }

      if (hasPrimaryModifier && ['u', 's', 'c', 'x'].includes(key)) {
        if (inEditable && ['c', 'x'].includes(key)) {
          return;
        }
        event.preventDefault();
      }
    };

    const onDragStart = (event: Event) => {
      if (isEditableTarget(event.target) || isAllowedDragTarget(event.target)) {
        return;
      }
      event.preventDefault();
    };

    document.addEventListener('contextmenu', preventDefault);
    document.addEventListener('copy', preventDefault);
    document.addEventListener('cut', preventDefault);
    document.addEventListener('selectstart', preventDefault);
    // document.addEventListener('dragstart', onDragStart);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.classList.remove('content-protection-enabled');
      document.removeEventListener('contextmenu', preventDefault);
      document.removeEventListener('copy', preventDefault);
      document.removeEventListener('cut', preventDefault);
      document.removeEventListener('selectstart', preventDefault);
      document.removeEventListener('dragstart', onDragStart);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isEnabled, isExcluded]);

  return null;
}
